import { Cell } from '@table-library/react-table-library/table';
import { showPlatform } from '../../../PromptManager/keys';
import { AI_TOOLS } from '../../../configs';
import DomainImage from '../../../../../components/DomainImage';
import PromptCountCell from './PromptCountCell';

// Platforms rendered as grouped column blocks. `state` is both the platform icon
// key and the field-name prefix the backend query (`ai-tracking-prompt-brand-by-platform`)
// attaches to every per-platform metric (e.g. `chat_gpt_mentions`, `perplexity_position`).
// `labelKey` reuses the existing prompt column translations for the platform name.
export const PLATFORMS = [
    { state: 'chat_gpt', labelKey: 'chatgpt' },
    { state: 'google_ai_mode', labelKey: 'google_ai_mode' },
    { state: 'google_ai_overview', labelKey: 'google_ai_overview' },
    { state: 'gemini', labelKey: 'gemini' },
    { state: 'perplexity', labelKey: 'perplexity' },
];

// Prompts for a platform arrive as a comma-separated string (`<state>_prompts`) or an
// array; `null` when the brand was not mentioned on that platform.
export const PROMPT_DELIMITER = ',';

export const parsePrompts = (raw) => {
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (typeof raw === 'string' && raw.trim()) {
        return raw
            .split(PROMPT_DELIMITER)
            .map((prompt) => prompt.trim())
            .filter(Boolean);
    }
    return [];
};

const sentimentClass = (value) => (value > 0.5 ? 'positive' : value >= 0 ? 'warning' : 'negative');

const baseColumns = (t) => [
    {
        label: t('ai_tracking.keyword_detail.brand_by_platform.columns.brand'),
        key: 'brand',
        data: 'brand',
        Component: Cell,
        sortType: 'string',
        cellSize: 'minmax(180px, 1.8fr)',
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;
            return <strong className="wrap-text">{data}</strong>;
        },
    },
    {
        label: t('ai_tracking.keyword_detail.brand_by_platform.columns.domain'),
        key: 'domain',
        data: 'domain',
        Component: Cell,
        cellSize: 'minmax(180px, 1.5fr)',
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return item.domain;
            return (
                <strong className="wrap-text d-flex align-items-center">
                    <DomainImage domain_name={item.domain} className="domain_img" />
                    <span>{item.domain}</span>
                </strong>
            );
        },
    },
    {
        label: t('ai_tracking.keyword_detail.brand_by_platform.columns.mentions'),
        key: 'mentions',
        data: 'mentions',
        Component: Cell,
    },
    {
        label: t('ai_tracking.keyword_detail.brand_by_platform.columns.platforms'),
        key: 'platforms',
        data: 'platforms',
        Component: Cell,
        sortType: 'string',
        cellSize: 'minmax(200px, 1.5fr)',
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;
            const platforms = data?.split(',') || [];
            return (
                <div className="d-flex flex-wrap gap-4">
                    {Object.keys(AI_TOOLS).map((state) => (
                        <div
                            key={state}
                            title={AI_TOOLS[state]}
                            className={`ai-tracking-platform-2 no-clickable ${
                                platforms?.includes(state) ? '' : 'disabled'
                            }`}
                        >
                            {showPlatform(state)}
                        </div>
                    ))}
                </div>
            );
        },
    },
];

const platformColumns = (t, isDark, { state }) => {
    const platformClass = isDark ? 'header-platform-column-dark' : 'header-platform-column';
    const mentionsKey = `${state}_mentions`;
    const positionKey = `${state}_position`;
    const sentimentKey = `${state}_sentiment`;
    const promptsKey = `${state}_prompts`;

    return [
        {
            label: t('ai_tracking.keyword_detail.brand_by_platform.columns.mentions'),
            key: mentionsKey,
            data: mentionsKey,
            Component: Cell,
            className: platformClass,
            renderCell: ({ data }) => data ?? 0,
        },
        {
            label: t('ai_tracking.keyword_detail.brand_by_platform.columns.avgPosition'),
            key: positionKey,
            data: positionKey,
            Component: Cell,
            className: platformClass,
            renderCell: ({ data }) => (data ? Number(data).toFixed(2) : '-'),
        },
        {
            label: t('ai_tracking.keyword_detail.brand_by_platform.columns.sentiment'),
            key: sentimentKey,
            data: sentimentKey,
            Component: Cell,
            className: platformClass,
            sortType: (array) =>
                array.sort(
                    (a, b) => Math.abs(a[sentimentKey] ?? 0) - Math.abs(b[sentimentKey] ?? 0),
                ),
            renderCell: ({ item, data, csvDownload }) => {
                // Sentiment defaults to 0 even when the brand was not mentioned on the
                // platform; only render a value when there is at least one mention.
                if (!item[mentionsKey] || data === null || data === undefined || data === '')
                    return '-';
                const value = Math.abs(Number(data));
                if (csvDownload) return value.toFixed(2);
                return (
                    <span className={`ai-column ${sentimentClass(Number(data))}`}>
                        {value.toFixed(2)}
                    </span>
                );
            },
        },
        {
            label: t('ai_tracking.keyword_detail.brand_by_platform.columns.prompts'),
            key: promptsKey,
            data: promptsKey,
            Component: Cell,
            className: platformClass,
            sortType: (array) =>
                array.sort(
                    (a, b) =>
                        parsePrompts(a[promptsKey]).length - parsePrompts(b[promptsKey]).length,
                ),
            renderCell: ({ item, csvDownload }) => {
                const prompts = parsePrompts(item[promptsKey]);
                if (csvDownload) return prompts.length;
                return <PromptCountCell prompts={prompts} />;
            },
        },
    ];
};

export const COLUMNS = (t, isDark) => [
    ...baseColumns(t),
    ...PLATFORMS.flatMap((platform) => platformColumns(t, isDark, platform)),
];

// Top header row: the four base columns get empty cells, then each platform gets
// one labelled cell (widened to visually span its four metric columns) followed by
// three empty spacer cells. Cell count must stay in lockstep with COLUMNS.
export const EXTRA_COLUMNS = (t, isDark) => [
    { label: '', key: 'brand', className: 'extraHeader' },
    { label: '', key: 'domain', className: 'extraHeader' },
    { label: '', key: 'mentions', className: 'extraHeader' },
    { label: '', key: 'platforms', className: 'extraHeader' },
    ...PLATFORMS.flatMap(({ state, labelKey }) => [
        {
            label: (
                <div className="ai-table-header">
                    {showPlatform(state)}{' '}
                    {t(`ai_tracking.keyword_detail.prompts.columns.${labelKey}`)}
                </div>
            ),
            key: `platform_${state}`,
            className: 'extraHeader',
            style: () => ({
                width: '480px',
                zIndex: 3,
                backgroundColor: isDark ? '#3d3f55' : '#f7f7f7',
            }),
        },
        { label: '', key: `${state}_spacer_2`, className: 'extraHeader' },
        { label: '', key: `${state}_spacer_3`, className: 'extraHeader' },
        { label: '', key: `${state}_spacer_4`, className: 'extraHeader' },
    ]),
];
